/** Returns each YYYY-MM-DD date from start to end, inclusive. Empty if start > end. */
export function eachDate(start: string, end: string): string[] {
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  if (startDate.getTime() > endDate.getTime()) return [];

  const dates: string[] = [];
  const cursor = new Date(startDate);
  while (cursor.getTime() <= endDate.getTime()) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

const DATE_FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_TRIP_DAYS = 366;

// Date's constructor rolls invalid day-of-month values over instead of
// erroring (e.g. 2026-02-30 silently becomes 2026-03-02) — Date.parse alone
// can't catch that, so re-check the components after construction. Built via
// setFullYear (not `new Date(y, m, d)`) because the constructor special-cases
// a 0-99 year as 1900+year; setFullYear does not.
function isRealCalendarDate(y: number, m: number, d: number): boolean {
  const date = new Date(0);
  date.setFullYear(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/**
 * Validates a trip's date range. Returns a Korean error message if invalid,
 * or null if the range is well-formed and no longer than MAX_TRIP_DAYS.
 */
export function validateTripDates(start: string, end: string): string | null {
  const startMatch = DATE_FORMAT.exec(start);
  const endMatch = DATE_FORMAT.exec(end);
  if (!startMatch || !endMatch) {
    return "날짜 형식이 올바르지 않습니다";
  }
  const [, sy, sm, sd] = startMatch;
  const [, ey, em, ed] = endMatch;
  if (
    !isRealCalendarDate(Number(sy), Number(sm), Number(sd)) ||
    !isRealCalendarDate(Number(ey), Number(em), Number(ed))
  ) {
    return "날짜 형식이 올바르지 않습니다";
  }
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  const diffDays = (endMs - startMs) / 86400000;
  if (diffDays >= MAX_TRIP_DAYS) {
    return "여행 기간은 최대 366일입니다";
  }
  return null;
}

/** "6시간 55분" (or "55분" under 60 minutes); null when durationMin is null. */
export function formatDuration(durationMin: number | null): string | null {
  if (durationMin === null) return null;
  const hours = Math.floor(durationMin / 60);
  const minutes = durationMin % 60;
  return hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`;
}

// depTime/arrTime/timeText are sort-load-bearing (day-card orders flights by
// depTime string comparison) — "6:05" must save as "06:05" so it sorts
// correctly against "12:00". Leaves anything else (already-padded, empty,
// malformed) untouched.
export function normalizeTime(value: string): string {
  return value.replace(/^(\d):(\d{2})$/, "0$1:$2");
}

/**
 * Formats a Date as YYYY-MM-DD using its local (getFullYear/Month/Date)
 * components — never UTC, which would shift the calendar day for anything
 * outside UTC+0.
 *
 * **문자열도 받는다.** 같은 컴포넌트를 Next 서버(진짜 `Date`)와 SPA(Worker가
 * 준 JSON — 날짜가 ISO 문자열)가 같이 쓴다. 타입만 `Date`로 두면 tsc는
 * 통과하고 브라우저에서 `getFullYear is not a function`으로 화면이 죽는다
 * (2026-09-27 관리자 화면).
 */
export function toLocalISO(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's date as YYYY-MM-DD in the local timezone (not UTC). */
export function todayLocalISO(): string {
  return toLocalISO(new Date());
}

/** Formats a Date as "YYYY-MM-DD HH:mm" using local time components -- same locality rule as toLocalISO, and takes strings for the same reason. Used by /admin/logs. */
export function toLocalDateTime(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${toLocalISO(date)} ${hours}:${minutes}`;
}

/**
 * Moves the day matching `todayISO` to the front, preserving the relative
 * order of the rest. Returns `days` unchanged when today isn't in the list
 * (outside the trip range, or no Day row for that date) or is already first.
 */
export function reorderDaysForToday<T extends { date: string }>(
  days: T[],
  todayISO: string
): T[] {
  const index = days.findIndex((d) => d.date === todayISO);
  if (index <= 0) return days;
  return [days[index], ...days.slice(0, index), ...days.slice(index + 1)];
}
